import { graphql, withApollo } from 'react-apollo'
import { withRouter } from 'react-router'

import { ModalActionTypes } from '@acorns/web-components'
import { pathOr } from 'ramda'
import { compose, withHandlers, withProps, withState } from 'recompose'

import { getRemoteStrings } from 'components/HOCs/withRemoteStrings/withRemoteStrings'
import { QuestionFlowType } from 'pages/LaterSuitabilityQuestions/types'
import { getLocalState } from 'pages/LaterSuitabilityQuestions/utils'
import createIRAWithProfileMutation from 'src/graphql/mutations/createIraWithProfile.mutation.gql'
import { withApolloErrorHandler } from 'utils/Apollo/apolloErrorHandlingProvider'
import { closeSession } from 'utils/auth'
import { withEnv } from 'utils/environment'
import { sendToSentry } from 'utils/errors'
import { withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'

import recommendedIraAccountQuery from './gql/recommendedIraAccount.query.gql'
import { Props } from './types'

const LATER_RECOMMENDATIONS = new Map([
  ['TraditionalIRA', 'Traditional'],
  ['SimplifiedEmployeePension', 'Simplified Employee Pension'],
  ['RothIRA', 'Roth'],
])

const hasLaterAccount = graphQLErrors => {
  const getErrors = pathOr([], [0, 'data', 'errors', 0])
  if (graphQLErrors) {
    return getErrors(graphQLErrors).includes('multiple_accounts_not_supported')
  }
}
export const createErrorModal = (customHandler, graphQLErrors) => {
  let errorDetails = {}
  if (hasLaterAccount(graphQLErrors)) {
    errorDetails = {
      actions: [
        {
          label: 'Okay',
          handler: () => {
            customHandler()
          },
          type: ModalActionTypes.primary,
        },
      ],
      title: 'You’ve already done this',
      message: 'It looks like you already opened up an Acorns Later account.',
    }
  }
  return errorDetails
}
export const enhance = compose<Props, {}>(
  withRouter,
  withApollo,
  withFlowRouter,
  withEnv,
  graphql(recommendedIraAccountQuery, {
    options: { fetchPolicy: 'network-only' },
  }),
  graphql(createIRAWithProfileMutation, { name: 'createIRAWithProfile' }),
  withState('creatingAccount', 'setCreatingAccount', false),
  withProps(({ data: { loading, recommendation } }) => {
    if (loading) {
      return { loading }
    } else {
      const recommended = LATER_RECOMMENDATIONS.get(recommendation)
      const headerText = recommended + ' IRA'
      const subHeader = getRemoteStrings(
        'later_ira_info_subTitle',
        'Based on your responses, this is the right IRA type for you',
        'laterOnboarding',
      )
      return { recommended, recommendation, headerText, subHeader }
    }
  }),
  withHandlers({
    handleNextPress: ({
      route,
      recommendation,
      client,
      createIRAWithProfile,
      setCreatingAccount,
    }) => () => {
      setCreatingAccount(true)
      const profileAttributes = getLocalState(client)
        .storedQuestions.filter(question => question.flowType === QuestionFlowType.Later)
        .map(laterQuestion => {
          return {
            key: laterQuestion.questionId,
            value: laterQuestion.answerId,
          }
        })
      const createIRAInvestmentAccount = async () => {
        return await createIRAWithProfile({
          variables: { iraType: recommendation, profileAttributes },
        })
      }
      createIRAInvestmentAccount()
        .then(() => {
          route({ from: FlowRoute.LaterIraRecommendation, action: 'continue' })
        })
        .catch(error => {
          sendToSentry(error)
          setCreatingAccount(false)
        })
    },
    handleCloseErrorModal: ({ env }) => () => {
      const url = env.get('webAppUrl') + '/invest/later'
      closeSession()
      window.location.replace(url)
    },
  }),
  withApolloErrorHandler(
    'createIRAWithProfile',
    ({ handleCloseErrorModal }) => ({ graphQLErrors = [] }) => {
      return createErrorModal(handleCloseErrorModal, graphQLErrors)
    },
  ),
)
